import type { BlockFootprint } from '@ranked-waiting/stack-tower-core';
import { worldToScreenY } from './stack-tower-camera';
import { drawStackTowerBackground } from './stack-tower-background';
import type { FallingPiece } from './stack-tower-physics';

/**
 * Phase 5 rendering: isometric-*ish* blocks (a cosmetic shear with a shaded
 * top and side face — not a real 3D camera, matching the locked 2D-canvas
 * decision) drawn over a procedural zone background (see
 * stack-tower-background.ts). Keeps the Phase 4 juice — falling debris,
 * bottom-anchored impact squash, gold perfect-hit flash, camera micro-shake.
 */
export interface SettledBlockView {
  readonly footprint: BlockFootprint;
  /** World Y of this block's bottom edge — 0 at the ground, increasing upward as the tower climbs. */
  readonly worldY: number;
}

/** A short-lived squash/flash animation on the block that was just placed. */
export interface ImpactView {
  /** Which settled block it applies to, matched by its `worldY`. */
  readonly worldY: number;
  /** Milliseconds since the placement landed. */
  readonly ageMs: number;
  readonly perfect: boolean;
  /**
   * The perfect streak reached BY this placement (0 for a non-perfect hit) —
   * drives the escalating flash tier (spec §11/§63: x1 small flash, x3
   * stronger pulse, x5 particles, x10 shimmer, x20 an aura around the tower
   * top). Ignored when `perfect` is false.
   */
  readonly streak: number;
  /**
   * A valid hit that barely survived — a tiny sliver, not a clean cut (spec
   * §12). Purely a feedback intensity signal; never changes hit/miss logic.
   */
  readonly nearMiss: boolean;
}

export interface StackTowerFrame {
  readonly width: number;
  readonly height: number;
  readonly trackWidth: number;
  readonly blockHeight: number;
  /** Blocks climbed since the last reset — drives the zone background (colour + parallax). Not score: score can outpace height once the multiplier (stack-tower-core's rules.ts) kicks in. Named `climbHeight`, not `height`, to stay distinct from this frame's own canvas-pixel `height` above. */
  readonly climbHeight: number;
  readonly cameraOffset: number;
  /** Camera micro-shake offset in screen px, applied to the whole game world (not the background). */
  readonly shakeX: number;
  readonly shakeY: number;
  readonly settledBlocks: readonly SettledBlockView[];
  readonly movingBlock: { readonly footprint: BlockFootprint; readonly worldY: number } | null;
  readonly fallingPieces: readonly FallingPiece[];
  readonly impact: ImpactView | null;
}

const SETTLED_BLOCK_COLOR = '#ff5c35'; // --colour-signal
const MOVING_BLOCK_COLOR = '#f3f1e9'; // --colour-paper
const PERFECT_FLASH_COLOR = '#ffd54a'; // warm gold — reads as "bonus", distinct from both block colours
const TRACK_MARGIN_PX = 32;

// Isometric constants — a *constant* cosmetic shear applied to every block
// regardless of sweep direction (an explicit simplification, per the plan).
// First-guess values, flagged for in-browser tuning.
const ISO_DEPTH_X = 0.44; // back-face horizontal shift, as a fraction of blockHeight
const ISO_DEPTH_Y = 0.5; // back-face vertical (upward) shift, as a fraction of blockHeight

const IMPACT_DURATION_MS = 190;
const PERFECT_FLASH_DURATION_MS = 320;
const IMPACT_MAX_SQUASH = 0.26; // peak vertical compression (and half that as horizontal bulge)

// Perfect-streak escalation (spec §11/§63) — each tier ADDS an effect on top
// of every lower tier, so streak 20 reads as "everything at once", not a
// replacement. Streak thresholds are cosmetic-only (never fed into score/
// leaderboard data), so they live here rather than in the pure core package.
const STREAK_TIER_2_PULSE = 3;
const STREAK_TIER_3_PARTICLES = 5;
const STREAK_TIER_4_SHIMMER = 10;
const STREAK_TIER_5_AURA = 20;
const PULSE_RING_DURATION_MS = 260;
const PARTICLE_COUNT = 6;
const PARTICLE_DURATION_MS = 300;
const PARTICLE_TRAVEL_PX = 26;
const SHIMMER_DURATION_MS = 260;
const AURA_DURATION_MS = 620;
const NEAR_MISS_FLASH_DURATION_MS = 260;
const NEAR_MISS_COLOR = '#ff8a3d'; // warm orange — distinct from both the gold perfect flash and the red miss debris

/**
 * The playable column's on-screen width and its left offset. The column is
 * capped to the SMALLER of the two viewport dimensions and centred, rather than
 * always spanning the full canvas width.
 *
 * Why (owner report 2026-08-18: "the blocks resize and move at different speed
 * if the phone is vertical or horizontal, it's really odd"): block width and
 * the moving block's sweep speed are both `worldUnits × (usableWidth /
 * trackWidth)`. When the horizontal scale keyed off the full canvas width, a
 * landscape (wide) canvas stretched every block AND — because the sweep covers
 * a fixed number of world units per second — sped the sweep up, while the
 * vertical size (blockHeight in px) stayed put. `min(width, height)` is
 * essentially invariant to rotating a given device (portrait width ≈ landscape
 * height), so keying the column off it makes the play-field the same size and
 * the sweep the same speed in both orientations. Portrait is unchanged to the
 * pixel (there `min(width, height) === width`); only wide canvases change,
 * centring the column with the spare width as side margin.
 */
function fieldMetrics(frame: StackTowerFrame): { usableWidth: number; offsetX: number } {
  const columnWidth = Math.min(frame.width, frame.height);
  const usableWidth = Math.max(1, columnWidth - 2 * TRACK_MARGIN_PX);
  const offsetX = (frame.width - usableWidth) / 2;
  return { usableWidth, offsetX };
}

function horizontalScale(frame: StackTowerFrame): number {
  return fieldMetrics(frame).usableWidth / frame.trackWidth;
}

function worldXToScreenX(worldX: number, frame: StackTowerFrame): number {
  const { usableWidth, offsetX } = fieldMetrics(frame);
  return offsetX + worldX * (usableWidth / frame.trackWidth);
}

function shade(hex: string, amount: number): string {
  const n = parseInt(hex.slice(1), 16);
  const clamp = (v: number) => Math.max(0, Math.min(255, Math.round(v)));
  const r = clamp(((n >> 16) & 255) + 255 * amount);
  const g = clamp(((n >> 8) & 255) + 255 * amount);
  const b = clamp((n & 255) + 255 * amount);
  return `rgb(${r},${g},${b})`;
}

/**
 * Draws one isometric block. The top face is lightened and the right face
 * darkened for a lit-from-above read; the shear is a constant screen-space
 * offset so it never depends on the block's width or position. An optional
 * bottom-anchored squash scales the whole solid around where it meets the
 * tower, so a placement reads as the block being pressed onto the stack.
 */
function drawBlock(
  ctx: CanvasRenderingContext2D,
  frame: StackTowerFrame,
  footprint: BlockFootprint,
  worldY: number,
  baseColor: string,
  squash: { readonly scaleX: number; readonly scaleY: number } | null,
): void {
  const botY = worldToScreenY(worldY, frame.cameraOffset, frame.height);
  const topY = botY - frame.blockHeight;
  const xMin = worldXToScreenX(footprint.min, frame);
  const xMax = worldXToScreenX(footprint.max, frame);
  const dx = frame.blockHeight * ISO_DEPTH_X;
  const dy = frame.blockHeight * ISO_DEPTH_Y;
  // Alternate a faint shade per tier so a perfectly-aligned stack of one
  // colour still reads as distinct strata instead of merging into one mass.
  const tier = Math.round(worldY / frame.blockHeight);
  const stripe = tier % 2 === 0 ? 0 : -0.08;

  ctx.save();
  if (squash) {
    const cx = (xMin + xMax) / 2;
    ctx.translate(cx, botY);
    ctx.scale(squash.scaleX, squash.scaleY);
    ctx.translate(-cx, -botY);
  }

  // Top face (lightest).
  ctx.fillStyle = shade(baseColor, 0.2 + stripe);
  ctx.beginPath();
  ctx.moveTo(xMin, topY);
  ctx.lineTo(xMax, topY);
  ctx.lineTo(xMax + dx, topY - dy);
  ctx.lineTo(xMin + dx, topY - dy);
  ctx.closePath();
  ctx.fill();

  // Right face (darkest).
  ctx.fillStyle = shade(baseColor, -0.28 + stripe);
  ctx.beginPath();
  ctx.moveTo(xMax, topY);
  ctx.lineTo(xMax + dx, topY - dy);
  ctx.lineTo(xMax + dx, botY - dy);
  ctx.lineTo(xMax, botY);
  ctx.closePath();
  ctx.fill();

  // Front face (base).
  ctx.fillStyle = shade(baseColor, stripe);
  ctx.fillRect(xMin, topY, Math.max(1, xMax - xMin), frame.blockHeight);

  ctx.restore();
}

function drawFallingPiece(ctx: CanvasRenderingContext2D, frame: StackTowerFrame, piece: FallingPiece): void {
  const scaleX = horizontalScale(frame);
  const screenCentreX = worldXToScreenX(piece.cx, frame);
  const screenCentreY = worldToScreenY(piece.cy, frame.cameraOffset, frame.height);
  const screenHalfW = piece.halfWidth * scaleX;
  const screenHalfH = piece.halfHeight; // world Y maps 1:1 to screen px in v1

  ctx.save();
  ctx.translate(screenCentreX, screenCentreY);
  ctx.rotate(piece.rotation);
  ctx.fillStyle = piece.color;
  ctx.fillRect(-screenHalfW, -screenHalfH, screenHalfW * 2, screenHalfH * 2);
  ctx.restore();
}

/** Bottom-anchored squash envelope: a quick ease-out decay, no bounce (a single overshoot reads cleaner at this size than a spring). */
function impactSquash(impact: ImpactView): { scaleX: number; scaleY: number } | null {
  if (impact.ageMs >= IMPACT_DURATION_MS) return null;
  const envelope = 1 - impact.ageMs / IMPACT_DURATION_MS; // 1 → 0
  return {
    scaleX: 1 + IMPACT_MAX_SQUASH * 0.5 * envelope,
    scaleY: 1 - IMPACT_MAX_SQUASH * envelope,
  };
}

/**
 * The base tier-1 flash every perfect gets (spec §11's "Perfect x1 small
 * flash") — a thin gold ring around the block, unchanged from Phase 3.
 */
function drawPerfectFlash(ctx: CanvasRenderingContext2D, frame: StackTowerFrame, xMin: number, xMax: number, bottomScreenY: number, ageMs: number): void {
  if (ageMs >= PERFECT_FLASH_DURATION_MS) return;
  ctx.save();
  ctx.globalAlpha = 1 - ageMs / PERFECT_FLASH_DURATION_MS;
  ctx.lineWidth = 3;
  ctx.strokeStyle = PERFECT_FLASH_COLOR;
  ctx.strokeRect(xMin, bottomScreenY - frame.blockHeight, Math.max(1, xMax - xMin), frame.blockHeight);
  ctx.restore();
}

/** Tier 2 (streak ≥3, spec's "stronger pulse"): a second ring expanding outward from the block and fading, on top of the base flash. */
function drawPulseRing(ctx: CanvasRenderingContext2D, frame: StackTowerFrame, xMin: number, xMax: number, bottomScreenY: number, ageMs: number): void {
  if (ageMs >= PULSE_RING_DURATION_MS) return;
  const t = ageMs / PULSE_RING_DURATION_MS; // 0 → 1
  const cx = (xMin + xMax) / 2;
  const cy = bottomScreenY - frame.blockHeight / 2;
  const baseRadius = (xMax - xMin) / 2;
  const radius = baseRadius + t * baseRadius * 0.9;
  ctx.save();
  ctx.globalAlpha = (1 - t) * 0.7;
  ctx.lineWidth = 2;
  ctx.strokeStyle = PERFECT_FLASH_COLOR;
  ctx.beginPath();
  ctx.ellipse(cx, cy, radius, radius * 0.55, 0, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
}

/** Tier 3 (streak ≥5, "subtle particles"): a handful of short sparks flying outward and fading — deterministic angles, no RNG needed for a handful of symmetric points. */
function drawPerfectParticles(ctx: CanvasRenderingContext2D, frame: StackTowerFrame, xMin: number, xMax: number, bottomScreenY: number, ageMs: number): void {
  if (ageMs >= PARTICLE_DURATION_MS) return;
  const t = ageMs / PARTICLE_DURATION_MS;
  const cx = (xMin + xMax) / 2;
  const cy = bottomScreenY - frame.blockHeight / 2;
  const travel = t * PARTICLE_TRAVEL_PX;
  ctx.save();
  ctx.globalAlpha = 1 - t;
  ctx.strokeStyle = PERFECT_FLASH_COLOR;
  ctx.lineWidth = 2;
  for (let i = 0; i < PARTICLE_COUNT; i += 1) {
    const angle = (i / PARTICLE_COUNT) * Math.PI * 2;
    const dx = Math.cos(angle);
    const dy = Math.sin(angle);
    ctx.beginPath();
    ctx.moveTo(cx + dx * travel, cy + dy * travel);
    ctx.lineTo(cx + dx * (travel + 6), cy + dy * (travel + 6));
    ctx.stroke();
  }
  ctx.restore();
}

/** Tier 4 (streak ≥10, "environmental shimmer"): a soft glow pulse behind the block, wide but low-opacity so it reads as ambient, not another ring. */
function drawShimmer(ctx: CanvasRenderingContext2D, frame: StackTowerFrame, xMin: number, xMax: number, bottomScreenY: number, ageMs: number): void {
  if (ageMs >= SHIMMER_DURATION_MS) return;
  const t = ageMs / SHIMMER_DURATION_MS;
  const cx = (xMin + xMax) / 2;
  const cy = bottomScreenY - frame.blockHeight / 2;
  const radius = (xMax - xMin) * 1.4;
  const gradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
  gradient.addColorStop(0, PERFECT_FLASH_COLOR);
  gradient.addColorStop(1, 'rgba(255, 213, 74, 0)');
  ctx.save();
  ctx.globalAlpha = (1 - t) * 0.35;
  ctx.fillStyle = gradient;
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

/** Tier 5 (streak ≥20, "brief aura around tower top"): a soft blurred ring, the longest-lived effect, layered on top of everything else. */
function drawAura(ctx: CanvasRenderingContext2D, frame: StackTowerFrame, xMin: number, xMax: number, bottomScreenY: number, ageMs: number): void {
  if (ageMs >= AURA_DURATION_MS) return;
  const t = ageMs / AURA_DURATION_MS;
  const cx = (xMin + xMax) / 2;
  const cy = bottomScreenY - frame.blockHeight / 2;
  const radius = (xMax - xMin) / 2 + 10;
  ctx.save();
  ctx.globalAlpha = (1 - t) * 0.8;
  ctx.filter = 'blur(6px)';
  ctx.lineWidth = 5;
  ctx.strokeStyle = PERFECT_FLASH_COLOR;
  ctx.beginPath();
  ctx.ellipse(cx, cy, radius, radius * 0.55, 0, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
}

/** Spec §12: a valid hit that barely survived gets a distinct, more dramatic flash — never changes whether it counted as a hit, purely feedback. */
function drawNearMissFlash(ctx: CanvasRenderingContext2D, frame: StackTowerFrame, xMin: number, xMax: number, bottomScreenY: number, ageMs: number): void {
  if (ageMs >= NEAR_MISS_FLASH_DURATION_MS) return;
  const t = ageMs / NEAR_MISS_FLASH_DURATION_MS;
  const cx = (xMin + xMax) / 2;
  const cy = bottomScreenY - frame.blockHeight / 2;
  const radius = 18 + t * 22;
  ctx.save();
  ctx.globalAlpha = (1 - t) * 0.85;
  ctx.lineWidth = 3;
  ctx.strokeStyle = NEAR_MISS_COLOR;
  ctx.beginPath();
  ctx.ellipse(cx, cy, radius, radius * 0.55, 0, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();
}

/** Escalates through every tier the streak has reached — each is additive, per spec's "Perfect x20 = brief aura" reading as everything-at-once, not a swap. */
function drawPerfectEscalation(ctx: CanvasRenderingContext2D, frame: StackTowerFrame, footprint: BlockFootprint, worldY: number, ageMs: number, streak: number): void {
  const bottomScreenY = worldToScreenY(worldY, frame.cameraOffset, frame.height);
  const xMin = worldXToScreenX(footprint.min, frame);
  const xMax = worldXToScreenX(footprint.max, frame);
  drawPerfectFlash(ctx, frame, xMin, xMax, bottomScreenY, ageMs);
  if (streak >= STREAK_TIER_2_PULSE) drawPulseRing(ctx, frame, xMin, xMax, bottomScreenY, ageMs);
  if (streak >= STREAK_TIER_3_PARTICLES) drawPerfectParticles(ctx, frame, xMin, xMax, bottomScreenY, ageMs);
  if (streak >= STREAK_TIER_4_SHIMMER) drawShimmer(ctx, frame, xMin, xMax, bottomScreenY, ageMs);
  if (streak >= STREAK_TIER_5_AURA) drawAura(ctx, frame, xMin, xMax, bottomScreenY, ageMs);
}

export function drawStackTowerFrame(ctx: CanvasRenderingContext2D, frame: StackTowerFrame): void {
  ctx.clearRect(0, 0, frame.width, frame.height);

  drawStackTowerBackground({
    ctx,
    width: frame.width,
    height: frame.height,
    cameraOffset: frame.cameraOffset,
    climbHeight: frame.climbHeight,
  });

  // Everything below is the game world, shaken as one unit; the background
  // above stays put so the shake reads as the camera kicking, not the scene.
  ctx.save();
  ctx.translate(frame.shakeX, frame.shakeY);

  for (const block of frame.settledBlocks) {
    const squash = frame.impact && frame.impact.worldY === block.worldY ? impactSquash(frame.impact) : null;
    drawBlock(ctx, frame, block.footprint, block.worldY, SETTLED_BLOCK_COLOR, squash);
    if (frame.impact && frame.impact.worldY === block.worldY) {
      if (frame.impact.perfect) {
        drawPerfectEscalation(ctx, frame, block.footprint, block.worldY, frame.impact.ageMs, frame.impact.streak);
      } else if (frame.impact.nearMiss) {
        const bottomScreenY = worldToScreenY(block.worldY, frame.cameraOffset, frame.height);
        const xMin = worldXToScreenX(block.footprint.min, frame);
        const xMax = worldXToScreenX(block.footprint.max, frame);
        drawNearMissFlash(ctx, frame, xMin, xMax, bottomScreenY, frame.impact.ageMs);
      }
    }
  }

  if (frame.movingBlock) {
    drawBlock(ctx, frame, frame.movingBlock.footprint, frame.movingBlock.worldY, MOVING_BLOCK_COLOR, null);
  }

  for (const piece of frame.fallingPieces) {
    drawFallingPiece(ctx, frame, piece);
  }

  ctx.restore();
}
